// @ts-ignore -- external typings not included
import _ from "lodash";
import { tableEnums } from "../../widgets/table/tableFeatures/tableEnums";
import { deepGet } from "../dataFunctions/dataFunctions";

import {
    startOfWeek,
    endOfWeek,
    startOfYesterday,
    startOfMonth,
    endOfMonth,
    subMonths,
    startOfYear,
    endOfYear,
    subYears,
    parse,
    parseISO,
    isValid as isValidDate,
    format as dfFormat,
} from "date-fns";

/** Строгий парсинг строки по формату */
function parseStrict(value: string, fmt: string): Date | null {
    const d = parse(value, fmt, new Date());
    // строгая проверка: форматируем обратно и сверяем с исходной строкой
    return isValidDate(d) && dfFormat(d, fmt) === value ? d : null;
}

/** Попытка парсинга как ISO (Z/+03:00/без зоны). */
function tryParseISO(value: string): Date | null {
    try {
        const d = parseISO(value);
        return isValidDate(d) ? d : null;
    } catch {
        return null;
    }
}

// функция для разделения разрядов числа
export function thousandSeparator(str: string) {
    let parts = (str + '').split('.'),
        main = parts[0],
        len = main.length,
        output = '',
        i = len - 1;

    while (i >= 0) {
        output = main.charAt(i) + output;
        if ((len - i) % 3 === 0 && i > 0) {
            output = ' ' + output;
        }
        --i;
    }

    if (parts.length > 1) {
        output += ',' + parts[1];
    }
    return output;
}

export function toNumberLikeString(str: any) {
    if (typeof str === 'string') {
        return str?.replace(',', '.')?.replace(/ /, '')
    }
}

// функция для капитализации первой буквы строки
export function capitalize(s: string) {
    return s && s[0].toUpperCase() + s.slice(1)
}

// Функция вычисления самой длинной строки из массива строк
export function getLongestString(arr: any[]) {
    return (
        arr.reduce((savedText: string | any[], text: string | any[]) => (text.length > savedText.length ? text : savedText), '')
    )
}

// функция для подсчета процента
export function calculatePercent(totalNumber: number, partialNumber: number) {
    if (totalNumber > 0) {
        return partialNumber / (totalNumber / 100)
    } else {
        return 0
    }
}

export function convertDate(str: any) {
    var date = new Date(str),
        month = ("0" + (date.getMonth() + 1)).slice(-2),
        day = ("0" + date.getDate()).slice(-2);
    return str ? [date.getFullYear(), month, day].join("-") : null
}

/**
 *  - "yyyy-MM-dd"             → "dd.MM.yyyy"
 *  - "yyyy-MM-dd'T'HH:mm"     → "dd.MM.yyyy HH:mm"
 *  - ISO (включая ...:ssZ)    → "dd.MM.yyyy HH:mm"
 */
export function formatDate(item: any) {
    if (!item) return null;
    const str = String(item);

    let d = parseStrict(str, 'yyyy-MM-dd');
    if (d) return dfFormat(d, 'dd.MM.yyyy');

    d = parseStrict(str, "yyyy-MM-dd'T'HH:mm");
    if (d) return dfFormat(d, 'dd.MM.yyyy HH:mm');

    d = tryParseISO(str);
    if (d) return dfFormat(d, 'dd.MM.yyyy HH:mm');

    return null;
}

export function checkToday(dates: any) {
    let today = convertDate(new Date())
    return dates ? convertDate(dates[0]) == today && convertDate(dates[1]) == today : false
}

export function checkYesterday(dates: any) {
    let yesterday = convertDate(startOfYesterday());
    return dates ? convertDate(dates[0]) == yesterday && convertDate(dates[1]) == yesterday : false
}

export function checkThisWeek(dates: any) {
    const today = new Date();
    const currentWeekMonday = startOfWeek(today, {
        weekStartsOn: 1
    }); // Monday as the start of the week
    const currentWeekSunday = endOfWeek(today, {
        weekStartsOn: 1
    });
    return dates ? convertDate(dates[0]) == convertDate(currentWeekMonday) && convertDate(dates[1]) == convertDate(currentWeekSunday) : false
}

export function checkLastWeek(dates: any) {
    const date = new Date()
    date.setDate(date.getDate() - 7)
    const lastWeekMonday = startOfWeek(date, {
        weekStartsOn: 1
    }); // Monday as the start of the week
    const lastWeekSunday = endOfWeek(date, {
        weekStartsOn: 1
    });
    return dates ? convertDate(dates[0]) == convertDate(lastWeekMonday) && convertDate(dates[1]) == convertDate(lastWeekSunday) : false
}

export function checkThisMonth(dates: any) {
    let today = new Date()
    const currentMonthStart = startOfMonth(today)
    const currentMonthEnd = endOfMonth(today)
    return dates ? convertDate(dates[0]) == convertDate(currentMonthStart) && convertDate(dates[1]) == convertDate(currentMonthEnd) : false
}

export function checkLastMonth(dates: any) {
    const date = subMonths(new Date, 1)
    const lastMonthStart = startOfMonth(date)
    const lastMonthEnd = endOfMonth(date)
    return dates ? convertDate(dates[0]) == convertDate(lastMonthStart) && convertDate(dates[1]) == convertDate(lastMonthEnd) : false
}

export function checkThisYear(dates: any) {
    let today = new Date()
    const currentYearStart = startOfYear(today)
    const currentYearEnd = endOfYear(today)
    return dates ? convertDate(dates[0]) == convertDate(currentYearStart) && convertDate(dates[1]) == convertDate(currentYearEnd) : false
}

export function checkLastYear(dates: any) {
    const date = subYears(new Date, 1)
    const lastYearStart = startOfYear(date)
    const lastYearEnd = endOfYear(date)
    return dates ? convertDate(dates[0]) == convertDate(lastYearStart) && convertDate(dates[1]) == convertDate(lastYearEnd) : false
}

// проверка на нахождение числа в строке 
export function formatNumbersInString(str: string): string {
    const regexp = /\d+\.\d{3,}/g;
    const matches = str.match(regexp);

    if (!matches) return str;

    for (const match of matches) {
        const replaced = match.replace('.', ',');
        str = str.replace(match, replaced);
    }

    return str;
}

// функция для форматирования данных таблицы (запятые в числах, даты)
export function prepareTableData(tableValue: any, columns: any, devExtreme?: boolean) {
    // определение списка текущих полей колонок таблицы
    let columnsFields: any = []
    columns.forEach((col: any) => {
        if (devExtreme) {
            columnsFields.push({
                fieldName: col.dataField ?? col.field,
                type: col.dataType ?? col.valueType,
                shouldBeFormatted: col.shouldBeFormatted !== false // default true unless explicitly false
            });
        } else {
            columnsFields.push({
                fieldName: col.field,
                type: col.valueType,
                shouldBeFormatted: col.shouldBeFormatted !== false
            });
        }
    });
    let newValue: any = []
    tableValue && tableValue.map((item: any) => {
        let newItem: any = { ...item }
        // обход по всем полям объекта
        columnsFields.map((columnField: any) => {
            const { fieldName, type, shouldBeFormatted } = columnField
            let currentValue = newItem[fieldName]
            if (fieldName?.includes('.')) {
                currentValue = deepGet(newItem, fieldName.split('.'))
            }
            let formatted = currentValue
            // Always attempt to format date strings regardless of shouldBeFormatted flag
            if (typeof currentValue === 'string' && type === 'date') {
                const dIsoCandidate = /[-T]/.test(currentValue) ? tryParseISO(currentValue) : null;
                const dIso = dIsoCandidate ||
                    parseStrict(currentValue, 'yyyy-MM-dd') ||
                    parseStrict(currentValue, "yyyy-MM-dd'T'HH:mm") ||
                    parseStrict(currentValue, 'yyyy-MM-ddX');
                if (dIso) {
                    formatted = dfFormat(dIso, currentValue.includes('T') ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy')
                }
            }
            // проверяем на наличие значения, иначе значение остается таким же (отсутствующим)
            if (currentValue != null) {
                if (!shouldBeFormatted) {
                    newItem[fieldName] = formatted
                } else {
                    if (typeof currentValue == 'string') {
                        formatted = formatNumbersInString(currentValue)
                        // Дата: обрабатываем только если колонка типа date ИЛИ строка реально похожа на дату
                        const looksLikeDate = (type === 'date') || /[-T]/.test(currentValue) || /^\d{4}-\d{2}-\d{2}/.test(currentValue)
                        if (looksLikeDate) {
                            // "YYYY-MM-DD" → "DD.MM.YYYY"
                            const dYmd = parseStrict(currentValue, 'yyyy-MM-dd')
                            if (dYmd) formatted = dfFormat(dYmd, 'dd.MM.yyyy')

                            // "YYYY-MM-DDZ" → "DD.MM.YYYY"
                            const dYmdZ = parseStrict(currentValue, 'yyyy-MM-ddX')
                            if (dYmdZ) formatted = dfFormat(dYmdZ, 'dd.MM.yyyy')

                            // "YYYY-MM-DDTHH:mm" → "DD.MM.YYYY HH:mm"
                            const dYmdHm = parseStrict(currentValue, "yyyy-MM-dd'T'HH:mm")
                            if (dYmdHm) formatted = dfFormat(dYmdHm, 'dd.MM.yyyy HH:mm')

                            // ISO → "DD.MM.YYYY HH:mm"
                            const dISO = /[-T]/.test(currentValue) ? tryParseISO(currentValue) : null
                            if (dISO) formatted = dfFormat(dISO, 'dd.MM.yyyy HH:mm')
                        }

                        if ((fieldName === 'MaintenancePosition' || fieldName === 'MaintenanceEnding') && currentValue.includes(';')) {
                            formatted = currentValue.split(';')[0]
                        }
                        if (fieldName === 'DefectClassificator') {
                            formatted = currentValue
                        }
                    } else if (typeof currentValue == 'number') {
                        // проверяем, если ли десятичная часть у числа, чтобы не добавлять лишних нулей после запятой
                        if (currentValue?.toString().indexOf(".") !== -1) {
                            formatted = thousandSeparator(currentValue.toFixed(3)?.toString()?.replace('-0.000', '0.000'))
                        } else {
                            formatted = thousandSeparator(currentValue?.toString())
                        }
                        if (fieldName.match(/Forward\dValue/) || fieldName.match(/Reverse\dValue/)) {
                            formatted = thousandSeparator(currentValue.toFixed(1)?.toString())
                        }
                        if (fieldName === "Position" || fieldName === 'Ending' || fieldName === 'startPosition' || fieldName === 'endPosition') {
                            formatted = thousandSeparator(currentValue.toFixed(3)?.toString()?.replace('-0.000', '0.000'))
                        }
                        if (fieldName === 'EventYear' || fieldName === 'Year' || fieldName === 'year') {
                            formatted = currentValue?.toString()
                        }
                        if (fieldName === 'Rating') {
                            formatted = currentValue.toFixed(1)
                        }
                    } else if (typeof currentValue == 'boolean') {
                        // проверяем, если ли десятичная часть у числа, чтобы не добавлять лишних нулей после запятой
                        if (fieldName === 'LessIsBetter') {
                            formatted = currentValue
                        }
                    }
                    if (fieldName?.includes('.')) {
                        _.set(newItem, fieldName, formatted)
                    } else {
                        newItem[fieldName] = formatted
                    }
                }
            }
        })
        // заполняем новый массив данных
        newValue.push(newItem)
    })
    return newValue
}

export function formatDateForFilter(date: any) {
    date = date?.replace(/\./g, '-')
    let newDate: any
    if (date.includes(':')) {
        newDate = date.split(' ')[0].split('-').reverse().join('-') + 'T' + date.split(' ')[1]
    } else {
        newDate = date.split('-').reverse().join('-')
    }
    return newDate
}

export function transformResponse(response: any) {
    let responseCopy: any = { value: [] }
    response && response.value.map((item: any) => {
        let itemCopy: any = {}
        Object.assign(itemCopy, item)
        Object.keys(tableEnums).map((key, index) => {
            if (itemCopy.hasOwnProperty(key) && itemCopy[key] !== null) {
                if (itemCopy[key] === false) {
                    itemCopy[key] = 0
                } else if (itemCopy[key] === true) {
                    itemCopy[key] = 1
                }
                itemCopy[key] = tableEnums[key][itemCopy[key]]
            }
        })
        responseCopy?.value.push(itemCopy)
    })
    return responseCopy
}

export function getValueWithMapLinks(value: any[], objectType: any) {
    return value && value.map((item: any) => ({ ...item, MapLink: `/app/map/${objectType}/${item.InfoObjectID}` }))
}

export function getValueWithObjectCardLinks(value: any[], objectType: any) {
    return value && value.map((item: any) => ({ ...item, ObjectCardLink: `/app/table/${objectType}/${item.InfoObjectID}` }))
}

export function transformArrayWithEnums(value: any) {
    value && value.map((item: any) => {
        Object.keys(tableEnums).map((key, index) => {
            if (item.hasOwnProperty(key) && item[key] !== null) {
                if (item[key] === false) {
                    item[key] = 0
                } else if (item[key] === true) {
                    item[key] = 1
                }
                item[key] = tableEnums[key][item[key]]
            }
        })
    })
    return value
}

export function transformObjectWithEnums(object: any) {
    if (!object) return {};
    Object.keys(tableEnums).map((key, index) => {
        if (object.hasOwnProperty(key) && object[key] !== null) {
            if (object[key] === false) {
                object[key] = 0
            } else if (object[key] === true) {
                object[key] = 1
            }
            object[key] = tableEnums[key][object[key]]
        }
    })
    return object
}

// функция для преобразования полученной строки геометрии объекта
export function removeEverySecondChar(inputString: string) {
    let result = '';
    for (let i = 0; i < inputString.length; i++) {
        if (i % 2 === 0) {
            result += inputString[i];
        }
    }
    return result;
}

// шаблон фильтра для получения уникальных значений в массиве
export function uniqueValues(value: any, index: any, array: string | any[]) {
    return array.indexOf(value) === index;
}

export const buildApiUrlQueryParams = (params: Record<string, any>) => {
    const queryParamsString = Object.entries(params)
        .filter(([, value]) => value != null)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
    return queryParamsString ? `?${queryParamsString}` : ''
};
